import { useAppSelector } from '@/store/hooks';
import { selectReportingV2Widget } from '@/store/slices/reportingV2/reportingV2Slice';
import { CompareArrowsRounded, InfoOutlined } from '@mui/icons-material';
import { Box, Button, Skeleton, Tooltip, Typography } from '@mui/material';
import { CTR_CLICKABLE_NOTE, compactCurrency, compactNum, currencyPrecise, percent, percentWhole } from '../format';
import { useExportWidgetCsv } from '../hooks/useExportWidgetCsv';
import { pctDelta, useClampedCurrentTotals, usePriorPeriodTotals } from '../hooks/usePriorPeriod';
import { useReportingPermissions } from '../hooks/useReportingPermissions';
import { COLORS, cardSx } from '../theme';
import Delta from './Delta';
import WidgetExportButton from './WidgetExportButton';

export const TILES: {
  metric: string;
  label: string;
  tooltip: string;
  format: (value: unknown) => string;
  derive?: (row: Record<string, unknown>) => unknown;
  /** Which direction is good. Omit for 'up'; cost metrics invert it. */
  goodDirection?: 'up' | 'down' | 'neutral';
}[] = [
  {
    metric: 'impressions',
    label: 'Impressions',
    tooltip: 'Total ad impressions served across all DSPs in the selected period.',
    format: compactNum,
  },
  {
    metric: 'spend',
    label: 'Spend',
    tooltip: 'Total media spend billed across all DSPs.',
    format: compactCurrency,
    goodDirection: 'neutral',
  },
  {
    metric: 'cpm',
    label: 'CPM',
    tooltip: 'Cost per thousand impressions: spend ÷ impressions × 1,000.',
    format: currencyPrecise,
    goodDirection: 'down',
    derive: (row: Record<string, unknown>) => {
      const imp = Number(row.impressions ?? 0);
      const spd = Number(row.spend ?? 0);
      return imp > 0 ? (spd / imp) * 1000 : null;
    },
  },
  {
    metric: 'ctr',
    label: 'CTR',
    tooltip: `Click-through rate: clicks ÷ impressions. ${CTR_CLICKABLE_NOTE}`,
    format: percent,
  },
  // Client Spend/CPM (#4446) are deliberately NOT a headline tile here — this
  // strip is exactly five cards (#4449/flank#4500), and Client Spend/CPM are
  // already visible, with the same permission gating, on GroupedBreakdownCard
  // and the per-platform breakdown table. Adding them here would either reopen
  // #4449 at seven tiles or collide with it outright (review, flank#4508).
  // Reach and Frequency are deliberately absent too, per #4449 — both remain
  // available in the compare table and the per-platform breakdown, this is
  // only about the headline strip. VCR takes Reach's slot, per #4109.
  {
    metric: 'vcr',
    label: 'VCR',
    tooltip: 'Video completion rate: completed views ÷ video impressions.',
    // Whole percent, no decimals — product meeting decision (prep #0000): "can
    // it just be 85%?". Every other rate tile keeps its own precision.
    format: percentWhole,
  },
];

interface KpiStripProps {
  onCompareToggle?: () => void;
  compareActive?: boolean;
}

/** Spend/CPM are our wholesale cost — gated behind "Show wholesale Spend and
 * CPM" (#4442). A card the user can't see is omitted entirely (#4636), not
 * rendered locked. */
const WHOLESALE_METRICS = new Set(['spend', 'cpm']);

/** Exported standalone so the omission rule is unit-testable without
 * mounting the strip (see KpiStrip.test.ts). */
export const visibleKpiTiles = (
  tiles: typeof TILES,
  canViewWholesaleSpend: boolean,
  restrictedFields: string[],
): typeof TILES =>
  tiles.filter(tile => {
    if (!WHOLESALE_METRICS.has(tile.metric)) return true;
    return canViewWholesaleSpend && !restrictedFields.includes(`metrics.${tile.metric}`);
  });

const KpiStrip = ({ onCompareToggle, compareActive }: KpiStripProps) => {
  const { rows, loading, error, restrictedFields } = useAppSelector(selectReportingV2Widget('totals'));
  const prior = usePriorPeriodTotals();
  // Freshness-clamped pair for the delta only (flank#4718) — `totals` above
  // (this tile's displayed figure) stays on the raw, unclamped filter dates,
  // so comparing it straight against the clamped prior window is what
  // inflated every delta when exactly one in-scope platform was behind.
  const comparisonCurrent = useClampedCurrentTotals();
  const { canViewWholesaleSpend } = useReportingPermissions();
  const totals = rows[0] ?? {};
  const priorTotals = prior.rows[0] ?? {};
  const comparisonTotals = comparisonCurrent.rows[0] ?? {};
  const comparisonLoading = prior.loading || comparisonCurrent.loading;
  const onExportCsv = useExportWidgetCsv('totals', rows);
  const visibleTiles = visibleKpiTiles(TILES, canViewWholesaleSpend, restrictedFields);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
        <WidgetExportButton onExport={onExportCsv} disabled={rows.length === 0} />
        {onCompareToggle && (
          <Button
            size='small'
            variant={compareActive ? 'contained' : 'outlined'}
            startIcon={<CompareArrowsRounded sx={{ fontSize: 16 }} />}
            onClick={onCompareToggle}
            disableElevation
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              fontSize: '0.78rem',
              ...(compareActive
                ? { bgcolor: COLORS.black, color: '#fff', '&:hover': { bgcolor: '#000' } }
                : { borderColor: COLORS.border, color: COLORS.text }),
            }}
          >
            Compare periods
          </Button>
        )}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(3, minmax(0, 1fr))',
            lg: 'repeat(5, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
        {visibleTiles.map(tile => {
          const rawValue = tile.derive ? tile.derive(totals as Record<string, unknown>) : totals[tile.metric];
          const value = tile.format(rawValue);
          const hasData = value !== '—';

          const rawComparisonCurrent = tile.derive
            ? tile.derive(comparisonTotals as Record<string, unknown>)
            : comparisonTotals[tile.metric];
          const rawPrior = tile.derive ? tile.derive(priorTotals as Record<string, unknown>) : priorTotals[tile.metric];
          const delta = pctDelta(rawComparisonCurrent, rawPrior);

          return (
            <Box
              className='rv2-card'
              key={tile.metric}
              sx={{
                ...cardSx,
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <Tooltip title={tile.tooltip} placement='top' arrow>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: COLORS.muted,
                      cursor: 'help',
                    }}
                  >
                    <Typography variant='body2' sx={{ fontWeight: 500 }}>
                      {tile.label}
                    </Typography>
                    <InfoOutlined sx={{ fontSize: 14 }} />
                  </Box>
                </Tooltip>
                {!loading && !error && !comparisonLoading && hasData && delta !== null && (
                  <Delta value={delta} goodDirection={tile.goodDirection} />
                )}
              </Box>

              {loading ? (
                <>
                  <Skeleton variant='text' width='70%' sx={{ fontSize: 30 }} />
                  <Skeleton variant='text' width='40%' />
                  <Skeleton variant='rectangular' width='100%' height={28} sx={{ borderRadius: 1, mt: 0.5 }} />
                </>
              ) : error ? (
                <Typography variant='body2' color='error' sx={{ mt: 0.5 }}>
                  {error}
                </Typography>
              ) : (
                <>
                  <Typography
                    data-testid={`kpi-value-${tile.metric}`}
                    sx={{ fontSize: 28, fontWeight: 700, color: COLORS.text, lineHeight: 1.1 }}
                  >
                    {value}
                  </Typography>
                  <Typography variant='caption' sx={{ color: COLORS.muted, display: 'block' }}>
                    {comparisonLoading
                      ? 'loading prior…'
                      : hasData && delta !== null
                        ? 'vs prior period'
                        : hasData
                          ? 'no prior data'
                          : '— —'}
                  </Typography>
                  {/*
                   * No sparkline here on purpose. This previously rendered a
                   * hardcoded [4,5,5,6,6,7,7] series on all six tiles, so a
                   * metric down 40% still drew a rising line. The `totals`
                   * widget has no per-day grain to draw a real one from, so the
                   * honest option is to show none — the delta above already
                   * carries the trend.
                   */}
                </>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default KpiStrip;
